/**
 * Serviço responsável pela limpeza completa de cache do navegador
 * Executa a remoção de cookies, localStorage, sessionStorage, Cache API
 * e desregistro de Service Workers de forma independente e resiliente.
 * Utiliza Promise.allSettled para degradação graceful — falhas individuais
 * não interrompem o fluxo geral de limpeza.
 */

/**
 * @typedef {Object} ResultadoLimpeza
 * @property {boolean} sucesso - true se ao menos uma etapa completou com sucesso
 * @property {string[]} etapasComSucesso - Lista de etapas que completaram com sucesso
 * @property {Array<{etapa: string, erro: Error}>} erros - Lista de erros ocorridos por etapa
 */

export class CacheCleanerService {
  /**
   * Executa todas as etapas de limpeza de cache de forma independente.
   * Cada etapa é executada em paralelo via Promise.allSettled, garantindo
   * que falhas individuais não interrompam as demais operações.
   * @returns {Promise<ResultadoLimpeza>} Resultado consolidado da operação
   */
  async executarLimpeza () {
    const etapas = [
      { nome: 'cookies', fn: () => this._limparCookies() },
      { nome: 'localStorage', fn: () => this._limparLocalStorage() },
      { nome: 'sessionStorage', fn: () => this._limparSessionStorage() },
      { nome: 'cacheAPI', fn: () => this._limparCacheAPI() },
      { nome: 'serviceWorker', fn: () => this._desregistrarServiceWorkers() }
    ]

    const resultados = await Promise.allSettled(
      etapas.map(etapa => etapa.fn())
    )

    const etapasComSucesso = []
    const erros = []

    resultados.forEach((resultado, index) => {
      const nomeEtapa = etapas[index].nome
      if (resultado.status === 'fulfilled') {
        etapasComSucesso.push(nomeEtapa)
      } else {
        const erro = resultado.reason
        erros.push({ etapa: nomeEtapa, erro })
        console.error(`Erro na limpeza do ${nomeEtapa}:`, erro)
      }
    })

    return {
      sucesso: etapasComSucesso.length > 0,
      etapasComSucesso,
      erros
    }
  }

  /**
   * Remove todos os cookies acessíveis via JavaScript no domínio atual.
   * Itera sobre cada cookie em document.cookie e define sua data de expiração
   * para o passado (Thu, 01 Jan 1970) com path=/ para garantir a remoção.
   * Cookies HttpOnly ou Secure inacessíveis via JS são ignorados automaticamente.
   * @returns {Promise<void>}
   * @throws {Error} Se ocorrer erro ao manipular document.cookie
   */
  async _limparCookies () {
    const cookies = document.cookie.split(';')

    for (const cookie of cookies) {
      const nomeCookie = cookie.split('=')[0].trim()
      if (nomeCookie) {
        document.cookie = `${nomeCookie}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`
      }
    }
  }

  /**
   * Limpa todos os dados armazenados no localStorage.
   * Chama localStorage.clear() para remover todas as chaves do domínio atual.
   * @returns {Promise<void>}
   * @throws {Error} Se localStorage.clear() falhar (ex: modo privado em alguns navegadores)
   */
  async _limparLocalStorage () {
    localStorage.clear()
  }

  /**
   * Limpa todos os dados armazenados no sessionStorage.
   * Chama sessionStorage.clear() para remover todas as chaves da sessão atual.
   * @returns {Promise<void>}
   * @throws {Error} Se sessionStorage.clear() falhar
   */
  async _limparSessionStorage () {
    sessionStorage.clear()
  }

  /**
   * Remove todos os caches registrados na Cache API do navegador.
   * Obtém a lista de caches via caches.keys() e remove cada um individualmente
   * via caches.delete(). Usado para invalidar cache offline de Service Workers.
   * @returns {Promise<void>}
   * @throws {Error} Se caches.keys() ou caches.delete() falhar
   */
  async _limparCacheAPI () {
    const nomesCaches = await caches.keys()
    await Promise.all(
      nomesCaches.map(nome => caches.delete(nome))
    )
  }

  /**
   * Desregistra todos os Service Workers registrados no navegador.
   * Verifica se o navegador suporta Service Workers antes de prosseguir.
   * Se não houver suporte, a etapa é ignorada sem gerar erro (degradação graceful).
   * Chama unregister() em cada registro encontrado via getRegistrations().
   * @returns {Promise<void>}
   * @throws {Error} Se getRegistrations() ou unregister() falhar
   */
  async _desregistrarServiceWorkers () {
    if (!navigator.serviceWorker) {
      return
    }

    const registros = await navigator.serviceWorker.getRegistrations()
    await Promise.all(
      registros.map(registro => registro.unregister())
    )
  }
}

// Instância singleton para uso na aplicação
export const cacheCleanerService = new CacheCleanerService()
